import React, { useState, useEffect } from 'react';
import { MapPin, Maximize2, Phone, Mail, Eye, X } from 'lucide-react';
import { getVacantBuildings } from '../services/api';

interface Vacant { id:number; name:string; type:string; location:string; area:string; floors:number; monthlyRent:string; contactPhone:string; contactEmail:string; features:string[]; availableFrom:string; }

const SubletSection: React.FC = () => {
  const [vacant, setVacant] = useState<Vacant[]>([]);
  const [selected, setSelected] = useState<Vacant | null>(null);
  const [search, setSearch] = useState('');
  useEffect(() => { getVacantBuildings().then(r => { if (r.data?.length) setVacant(r.data); }).catch(()=>{}); }, []);
  const filtered = vacant.filter(v => v.name.toLowerCase().includes(search.toLowerCase()) || v.location.toLowerCase().includes(search.toLowerCase()) || v.type.toLowerCase().includes(search.toLowerCase()));

  return (
    <section className="sublet-section section-py" id="sublet">
      <div className="container">
        <div className="sublet-intro">
          <div>
            <div style={{ fontFamily:'var(--font-condensed)', fontSize:'0.78rem', fontWeight:700, letterSpacing:'0.16em', textTransform:'uppercase', color:'rgba(255,255,255,0.7)', marginBottom:'8px' }}>Housing Agency Portal</div>
            <h3>Vacant Buildings Available for Sublet</h3>
            <p>NIDAVE manages a portfolio of premium commercial and industrial spaces. Browse current vacancies and contact us for viewings and terms.</p>
          </div>
          <div style={{ background:'rgba(255,255,255,0.2)', borderRadius:'8px', padding:'16px 24px', textAlign:'center', flexShrink:0 }}>
            <div style={{ fontFamily:'var(--font-display)', fontSize:'2.8rem', color:'white', lineHeight:1 }}>{vacant.length}</div>
            <div style={{ fontFamily:'var(--font-condensed)', fontSize:'0.72rem', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'rgba(255,255,255,0.75)', marginTop:'4px' }}>Units Available</div>
          </div>
        </div>

        <div style={{ marginBottom:'20px' }}>
          <input className="form-input" placeholder="Search by name, location, or type..." value={search} onChange={e=>setSearch(e.target.value)} style={{ maxWidth:'440px' }} />
        </div>

        <div className="sublet-table-wrapper">
          <table className="sublet-table">
            <thead>
              <tr><th>Property</th><th>Type</th><th>Location</th><th>Area</th><th>Monthly Rent</th><th>Available</th><th>Action</th></tr>
            </thead>
            <tbody>
              {filtered.map(v => (
                <tr key={v.id}>
                  <td>
                    <div className="building-name-cell">{v.name}</div>
                    <div style={{ display:'flex', gap:'5px', marginTop:'5px', flexWrap:'wrap' }}>
                      {(v.features || []).slice(0,3).map(f => (
                        <span key={f} style={{ padding:'2px 8px', background:'rgba(232,93,4,0.08)', color:'var(--brand-orange)', borderRadius:'50px', fontSize:'0.7rem', fontFamily:'var(--font-condensed)', fontWeight:700 }}>{f}</span>
                      ))}
                    </div>
                  </td>
                  <td>{v.type}</td>
                  <td><span style={{ display:'flex', alignItems:'center', gap:'5px' }}><MapPin size={13} style={{ color:'var(--brand-orange)' }} />{v.location}</span></td>
                  <td><span style={{ display:'flex', alignItems:'center', gap:'5px' }}><Maximize2 size={13} />{v.area}</span></td>
                  <td><span style={{ fontFamily:'var(--font-condensed)', fontWeight:700, color:'#40916c', fontSize:'0.98rem' }}>{v.monthlyRent}</span><span style={{ color:'var(--text-muted)', fontSize:'0.76rem' }}>/mo</span></td>
                  <td><span style={{ padding:'3px 10px', background:'rgba(45,106,79,0.12)', color:'#40916c', borderRadius:'50px', fontFamily:'var(--font-condensed)', fontWeight:700, fontSize:'0.78rem' }}>{v.availableFrom}</span></td>
                  <td><button className="action-btn action-btn-edit" onClick={()=>setSelected(v)}><Eye size={13} /> Details</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && <div style={{ padding:'40px', textAlign:'center', color:'var(--text-muted)', fontFamily:'var(--font-condensed)' }}>No properties match your search.</div>}
        </div>
      </div>

      {selected && (
        <div className="modal-overlay" onClick={()=>setSelected(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{selected.name}</h3>
              <button className="modal-close" onClick={()=>setSelected(null)}><X size={16} /></button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'20px' }}>
              {[['Type',selected.type],['Location',selected.location],['Area',selected.area],['Floors',`${selected.floors} floor${selected.floors!==1?'s':''}`],['Monthly Rent',selected.monthlyRent],['Available From',selected.availableFrom]].map(([label,value]) => (
                <div key={label} style={{ padding:'12px', background:'var(--bg-secondary)', borderRadius:'6px' }}>
                  <div style={{ fontFamily:'var(--font-condensed)', fontSize:'0.72rem', fontWeight:700, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--text-muted)', marginBottom:'4px' }}>{label}</div>
                  <div style={{ fontWeight:600, color:'var(--text-primary)', fontSize:'0.92rem' }}>{value}</div>
                </div>
              ))}
            </div>
            <div style={{ marginBottom:'20px' }}>
              <div className="section-label" style={{ marginBottom:'10px' }}>Features</div>
              <div style={{ display:'flex', gap:'8px', flexWrap:'wrap' }}>
                {(selected.features || []).map(f => <span key={f} style={{ padding:'5px 14px', background:'rgba(232,93,4,0.1)', border:'1px solid rgba(232,93,4,0.2)', color:'var(--brand-orange)', borderRadius:'50px', fontFamily:'var(--font-condensed)', fontWeight:700, fontSize:'0.8rem' }}>{f}</span>)}
              </div>
            </div>
            <div style={{ borderTop:'1px solid var(--border)', paddingTop:'18px' }}>
              <div className="section-label" style={{ marginBottom:'12px' }}>Contact</div>
              <div style={{ display:'flex', flexDirection:'column', gap:'10px', marginBottom:'20px' }}>
                <a href={`tel:${selected.contactPhone}`} style={{ display:'flex', alignItems:'center', gap:'10px', color:'var(--text-primary)', textDecoration:'none' }}><Phone size={15} style={{ color:'var(--brand-orange)' }} />{selected.contactPhone}</a>
                <a href={`mailto:${selected.contactEmail}`} style={{ display:'flex', alignItems:'center', gap:'10px', color:'var(--text-primary)', textDecoration:'none' }}><Mail size={15} style={{ color:'var(--brand-orange)' }} />{selected.contactEmail}</a>
              </div>
              <a href="#contact" className="btn-primary" style={{ width:'100%', justifyContent:'center' }} onClick={()=>setSelected(null)}>Enquire About This Property</a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
export default SubletSection;
